import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  FaWallet,
  FaRupeeSign,
  FaArrowUp,
  FaArrowDown,
  FaUsers,
  FaClock,
  FaEye,
  FaEyeSlash,
  FaHistory,
  FaPlus,
  FaSync,
  FaChartLine,
  FaMoneyBillWave,
  FaPercentage,
} from "react-icons/fa";
import {
  getVendorWalletSummary,
  COMMISSION_STRUCTURE,
  updateDriverWalletBalance,
} from "../../features/vendor/vendorWalletSlice";
import { toast } from "react-toastify";
import axios from "axios";
import { API_URL } from "../../config/apiConfig";
import socketService from "../../utils/socketService";

const VendorWalletCard = ({ vendorId, onNavigateToWallet, commissionOnly = false }) => {
  const dispatch = useDispatch();
  const {
    driverWallets,
    totalWalletBalance,
    totalCommissionEarned,
    totalDriverEarnings,
    pendingCommissions,
    loading,
    error,
    lastUpdated,
  } = useSelector((state) => state.vendorWallet);

  const [showBalances, setShowBalances] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [realTimeUpdateCount, setRealTimeUpdateCount] = useState(0);
  const [commissionData, setCommissionData] = useState(null);
  const [loadingCommission, setLoadingCommission] = useState(true);

  // Fetch wallet data on component mount and vendor change
  useEffect(() => {
    if (vendorId) {
      dispatch(getVendorWalletSummary(vendorId));
      fetchCommissionData();
    }
  }, [dispatch, vendorId]);

  const fetchCommissionData = async () => {
    try {
      const token = localStorage.getItem('vendorToken');

      if (!token) {
        setLoadingCommission(false);
        return;
      }

      // Get real commission data from vendor settings
      const response = await axios.get(
        `${API_URL}/vendor/${vendorId}/commission/settings`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success || response.data.status) {
        setCommissionData(response.data.data);
      } else {
      }
    } catch (error) {
      console.error('❌ VendorWalletCard: Error fetching commission data:', error);
    } finally {
      setLoadingCommission(false);
    }
  };

  // Set up real-time socket listeners for wallet updates
  useEffect(() => {
    if (!vendorId) return;


    // Connect and join vendor room using proper socket service
    socketService.connect();
    socketService.joinVendorRoom(vendorId);

    // Listen for wallet updates from any of vendor's drivers
    const handleWalletUpdate = (data) => {

      // Check if this update is relevant to this vendor
      if (data.vendorId === vendorId ||
          (data.driverId && driverWallets.some(dw => dw.driverId === data.driverId))) {

        setRealTimeUpdateCount(prev => prev + 1);

        // Update specific driver's wallet balance if available
        if (data.driverId && data.newBalance !== undefined) {
          dispatch(updateDriverWalletBalance({
            driverId: data.driverId,
            newBalance: data.newBalance
          }));
        }

        // Refresh all wallet data to ensure consistency
        setTimeout(() => {
          dispatch(getVendorWalletSummary(vendorId));
        }, 1000);

        // Show notification for significant updates
        if (data.type && data.amount) {
          const driver = driverWallets.find(dw => dw.driverId === data.driverId);
          const driverName = driver ? driver.driverName : 'Driver';

          switch (data.type) {
            case 'commission_processed':
              toast.info(`💰 Commission processed for ${driverName}: ₹${data.amount}`);
              break;
            case 'ride_earning':
              toast.success(`🚗 Ride completed by ${driverName}: ₹${data.amount}`);
              break;
            case 'withdrawal_approved':
              toast.success(`✅ Withdrawal approved for ${driverName}: ₹${data.amount}`);
              break;
            case 'withdrawal_request':
              toast.info(`📤 Withdrawal requested by ${driverName}: ₹${data.amount}`);
              break;
            default:
              break;
          }
        }
      }
    };

    // Subscribe to relevant wallet events using proper socket service
    const cleanupWalletUpdated = socketService.listenForEvent('walletUpdated', handleWalletUpdate);
    const cleanupCommissionProcessed = socketService.listenForEvent('commissionProcessed', handleWalletUpdate);
    const cleanupWithdrawalUpdated = socketService.listenForEvent('withdrawalUpdated', handleWalletUpdate);

    // Cleanup function
    return () => {
      if (cleanupWalletUpdated) cleanupWalletUpdated();
      if (cleanupCommissionProcessed) cleanupCommissionProcessed();
      if (cleanupWithdrawalUpdated) cleanupWithdrawalUpdated();
    };
  }, [vendorId, driverWallets, dispatch]);

  // Auto-refresh wallet data every 30 seconds
  useEffect(() => {
    if (!vendorId) return;

    const refreshInterval = setInterval(() => {
      dispatch(getVendorWalletSummary(vendorId));
    }, 30000);

    return () => clearInterval(refreshInterval);
  }, [vendorId, dispatch]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await dispatch(getVendorWalletSummary(vendorId)).unwrap();
      toast.success('💰 Wallet data refreshed successfully');
    } catch (error) {
      toast.error('Failed to refresh wallet data');
    } finally {
      setRefreshing(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const formatPercentage = (value) => {
    const num = Number(value);
    return Number.isFinite(num) ? `${num}%` : 'N/A';
  };

  const calculateCommissionRate = () => {
    // Use real commission rate from API data, not hardcoded values
    if (!commissionData) {
      return 0; // Return 0 while loading instead of hardcoded fallback
    }

    const effectiveRate = commissionData.commissionSettings?.effectiveRate ||
                         commissionData.commissionStructure?.vendorRate || 0;


    return effectiveRate;
  };

  const getWalletStatusColor = () => {
    if (totalWalletBalance >= 1000) return "text-green-600";
    if (totalWalletBalance >= 0) return "text-blue-600";
    return "text-red-600";
  };

  const getWalletStatusIcon = () => {
    if (totalWalletBalance >= 1000) return <FaArrowUp className="w-5 h-5 text-green-500" />;
    if (totalWalletBalance >= 0) return <FaWallet className="w-5 h-5 text-blue-500" />;
    return <FaArrowDown className="w-5 h-5 text-red-500" />;
  };

  if ((loading && !driverWallets.length) || loadingCommission) {
    return (
      <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100 animate-pulse">
        <div className="flex items-center justify-between mb-6">
          <div className="h-6 w-40 bg-gray-200 rounded"></div>
          <div className="w-8 h-8 bg-gray-200 rounded-full"></div>
        </div>
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div className="h-20 bg-gray-200 rounded"></div>
          <div className="h-20 bg-gray-200 rounded"></div>
        </div>
        <div className="h-16 bg-gray-200 rounded">
          <div className="p-2 text-xs text-gray-500">Loading real commission data...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-purple-50 to-indigo-100 rounded-xl shadow-lg p-6 border border-purple-200">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <div className="p-3 rounded-full bg-purple-100 text-purple-600 mr-3">
            <FaWallet className="w-6 h-6" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-800">Vendor Wallet</h3>
            <p className="text-sm text-gray-600">
              Commission Rate: {loadingCommission ? 'Loading...' : `${formatPercentage(calculateCommissionRate())} per ride`}
              {realTimeUpdateCount > 0 && (
                <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">
                  Live: {realTimeUpdateCount} updates
                </span>
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setShowBalances(!showBalances)}
            className="p-2 rounded-full hover:bg-purple-200 transition-colors"
            title={showBalances ? "Hide balances" : "Show balances"}
          >
            {showBalances ? (
              <FaEye className="w-4 h-4 text-purple-600" />
            ) : (
              <FaEyeSlash className="w-4 h-4 text-purple-600" />
            )}
          </button>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="p-2 rounded-full hover:bg-purple-200 transition-colors disabled:opacity-50"
            title="Refresh wallet data"
          >
            <FaSync className={`w-4 h-4 text-purple-600 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Main Wallet Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        {/* Total Wallet Balance - Show commission amount only if commissionOnly prop is true */}
        <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-600">
              {commissionOnly ? 'Total Commission Balance' : 'Total Wallet Balance'}
            </span>
            {getWalletStatusIcon()}
          </div>
          <div className="flex items-center">
            <span className={`text-2xl font-bold ${getWalletStatusColor()}`}>
              {showBalances ? formatCurrency(commissionOnly ? totalCommissionEarned : totalWalletBalance) : "₹****"}
            </span>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            {commissionOnly ? 'Commission from rides' : `Across ${driverWallets.length} drivers`}
          </p>
        </div>

        {/* Commission Earned - Real data from API */}
        <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-600">Commission Earned</span>
            <FaPercentage className="w-4 h-4 text-green-500" />
          </div>
          <div className="flex items-center">
            <span className="text-2xl font-bold text-green-600">
              {showBalances ? formatCurrency(totalCommissionEarned) : "₹****"}
            </span>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            {formatPercentage(calculateCommissionRate())} from total rides
          </p>
        </div>
      </div>

      {/* Additional Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div className="bg-white rounded-lg p-3 text-center border border-gray-100">
          <div className="flex items-center justify-center mb-1">
            <FaMoneyBillWave className="w-4 h-4 text-blue-500 mr-1" />
            <span className="text-xs text-gray-600">Driver Earnings</span>
          </div>
          <p className="font-semibold text-blue-600 text-sm">
            {showBalances ? formatCurrency(totalDriverEarnings) : "₹****"}
          </p>
        </div>

        <div className="bg-white rounded-lg p-3 text-center border border-gray-100">
          <div className="flex items-center justify-center mb-1">
            <FaClock className="w-4 h-4 text-orange-500 mr-1" />
            <span className="text-xs text-gray-600">Pending</span>
          </div>
          <p className="font-semibold text-orange-600 text-sm">
            {showBalances ? formatCurrency(pendingCommissions) : "₹****"}
          </p>
        </div>

        <div className="bg-white rounded-lg p-3 text-center border border-gray-100">
          <div className="flex items-center justify-center mb-1">
            <FaUsers className="w-4 h-4 text-indigo-500 mr-1" />
            <span className="text-xs text-gray-600">Active Drivers</span>
          </div>
          <p className="font-semibold text-indigo-600 text-sm">
            {driverWallets.filter(dw => dw.isActive).length}
          </p>
        </div>

        <div className="bg-white rounded-lg p-3 text-center border border-gray-100">
          <div className="flex items-center justify-center mb-1">
            <FaChartLine className="w-4 h-4 text-purple-500 mr-1" />
            <span className="text-xs text-gray-600">Available</span>
          </div>
          <p className="font-semibold text-purple-600 text-sm">
            {driverWallets.filter(dw => dw.isAvailable).length}
          </p>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="grid grid-cols-2 gap-3">
        <button
          onClick={() => onNavigateToWallet && onNavigateToWallet('wallet')}
          className="bg-purple-500 hover:bg-purple-600 text-white py-3 px-4 rounded-lg font-medium transition-colors flex items-center justify-center"
        >
          <FaHistory className="mr-2" />
          Wallet Details
        </button>
        
        <button
          onClick={() => onNavigateToWallet && onNavigateToWallet('commission')}
          className="bg-green-500 hover:bg-green-600 text-white py-3 px-4 rounded-lg font-medium transition-colors flex items-center justify-center"
        >
          <FaArrowUp className="mr-2" />
          Request Withdrawal
        </button>
      </div>

      {/* Commission Structure Info - Real Data */}
      <div className="mt-4 p-3 bg-purple-50 rounded-lg border border-purple-200">
        <h4 className="text-sm font-medium text-purple-800 mb-2">Commission Structure</h4>
        {commissionData ? (
          <div className="text-xs text-purple-700 space-y-1">
            <div className="flex justify-between">
              <span>Admin Commission:</span>
              <span className="font-medium">{formatPercentage(commissionData.commissionStructure?.adminRate || 0)}</span>
            </div>
            <div className="flex justify-between">
              <span>Vendor Commission (You):</span>
              <span className="font-medium text-purple-900">{formatPercentage(calculateCommissionRate())}</span>
            </div>
            <div className="flex justify-between">
              <span>Driver Gets:</span>
              <span className="font-medium">{formatPercentage(commissionData.commissionStructure?.driverRate || 0)}</span>
            </div>
            <div className="mt-2 pt-2 border-t border-purple-200">
              <div className="text-xs text-purple-600">
                ✅ Real Data Source: {commissionData.commissionSettings?.isUsingGlobal ? 'Admin Global Settings' : 'Vendor Specific Settings'}
              </div>
              <div className="text-xs text-purple-500 mt-1">
                Updated from live admin system (not hardcoded)
              </div>
            </div>
          </div>
        ) : (
          <div className="text-xs text-purple-700">
            <div className="animate-pulse">Loading real commission data...</div>
          </div>
        )}
      </div>

      {/* Last Updated */}
      {lastUpdated && (
        <p className="text-xs text-gray-500 mt-3 text-center">
          Last updated: {new Date(lastUpdated).toLocaleTimeString()}
        </p>
      )}

      {/* Error Display */}
      {error && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-600">{error}</p>
          <button
            onClick={handleRefresh}
            className="text-red-600 hover:text-red-700 text-xs underline mt-1"
          >
            Try again
          </button>
        </div>
      )}
    </div>
  );
};

export default VendorWalletCard;
